import type { AccessSchedule, AccessSettings } from '@/models/channel';
import { AccessLevelType } from '@/models/channel';
import { AssetSource, Permission } from '@/enums';
import type { Permissions } from '@/models/Permissions';
import type { ContentSharingBehaviors, OU } from '@/models/Ou';
import type { AssetForm } from '@/store/forms';
import { SchemaType } from '@/store/forms/Asset-Editor/SchemaTypeOptions';
import { DateTime } from 'luxon';

export interface AccessLevelOption {
  label: string;
  value: AccessLevelType;
}

export const DefaultAccessLevelOptions: AccessLevelOption[] = [
  { label: 'Free', value: AccessLevelType.Free },
  { label: 'Private', value: AccessLevelType.Private },
];

export const AllAccessLevelOptions: AccessLevelOption[] = [
  { label: 'Free', value: AccessLevelType.Free },
  { label: 'Paywall', value: AccessLevelType.Paywall },
  { label: 'Private', value: AccessLevelType.Private },
];

/** Returns true when a schedule bound is empty (meaning beginning or end of time). */
const isOpenScheduleBound = (value: string | null | undefined): boolean => value == null || value === '';

/** Returns true when two access schedule time ranges overlap. */
const schedulesOverlap = (a: AccessSchedule, b: AccessSchedule): boolean => {
  const aFrom = isOpenScheduleBound(a.from) ? null : DateTime.fromISO(a.from);
  const aTo = isOpenScheduleBound(a.to) ? null : DateTime.fromISO(a.to);
  const bFrom = isOpenScheduleBound(b.from) ? null : DateTime.fromISO(b.from);
  const bTo = isOpenScheduleBound(b.to) ? null : DateTime.fromISO(b.to);

  if (aFrom && bTo && aFrom > bTo) {
    return false;
  }
  if (bFrom && aTo && bFrom > aTo) {
    return false;
  }
  return true;
};

const accessUtils = {
  /** @internal Updates access schedules for an expire date change; use applyExpireSet instead. */
  ensureExpireSet: (newAccessSchedules: AccessSchedule[], oldExpire: string, newExpire: string, force = false) => {
    const newSchedule: AccessSchedule = { from: newExpire, to: null, accessLevel: AccessLevelType.Private };
    if (oldExpire == null) {
      // we will need to create a new access schedule if newExpire is not null
      if (newExpire != null) {
        newAccessSchedules.push(newSchedule);
      }
    } else {
      const foundIndex = newAccessSchedules.findIndex((x) => x.from === oldExpire);
      if (foundIndex > -1) {
        if (newExpire == null) {
          // remove the thing from the list
          newAccessSchedules.splice(foundIndex, 1);
        } else {
          // console.log('updating newAccessSchedules at ', foundIndex);
          newAccessSchedules[foundIndex] = newSchedule;
        }
      } else if (force) {
        newAccessSchedules.push(newSchedule);
      }
    }
    return newAccessSchedules;
  },

  /** @internal Updates access schedules for an embargo date change; use applyEmbargoSet instead. */
  ensureEmbargoSet: (
    newAccessSchedules: AccessSchedule[],
    oldEmbargo: string,
    newEmbargo: string,
    force = false
  ): AccessSchedule[] => {
    const newSchedule: AccessSchedule = { from: null, to: newEmbargo, accessLevel: AccessLevelType.Private };
    if (oldEmbargo == null) {
      // we will need to create a new access schedule if newEmbargo is not null
      if (newEmbargo != null) {
        // console.log('inserting new access schedule at position 0', { newSchedule });
        newAccessSchedules.unshift(newSchedule);
      }
    } else {
      const foundIndex = newAccessSchedules.findIndex((x) => x.to === oldEmbargo);
      if (foundIndex > -1) {
        // console.log('updating newAccessSchedules at ', foundIndex);
        if (newEmbargo == null) {
          // remove the thing from the list
          newAccessSchedules.splice(foundIndex, 1);
        } else {
          newAccessSchedules[foundIndex] = newSchedule;
        }
      } else if (force) {
        // console.log('schedule not found, inserting new access schedule at position 0', { newSchedule });
        newAccessSchedules.unshift(newSchedule);
      }
    }
    return newAccessSchedules;
  },

  /** Returns an overlap error message if the proposed embargo date change is invalid, otherwise null. */
  validateEmbargoSetChange: (
    schedules: AccessSchedule[],
    oldEmbargo: string | null,
    newEmbargo: string | null,
    force = false
  ): string | null => {
    if (newEmbargo == null) {
      return null;
    }

    const newSchedule: AccessSchedule = { from: null, to: newEmbargo, accessLevel: AccessLevelType.Private };
    let replaceIndex: number | undefined;

    if (oldEmbargo != null) {
      const foundIndex = schedules.findIndex((x) => x.to === oldEmbargo);
      if (foundIndex > -1) {
        replaceIndex = foundIndex;
      } else if (!force) {
        return null;
      }
    }

    return accessUtils.validateScheduleOverlap(schedules, newSchedule, replaceIndex);
  },

  /** Returns an overlap error message if the proposed expire date change is invalid, otherwise null. */
  validateExpireSetChange: (
    schedules: AccessSchedule[],
    oldExpire: string | null,
    newExpire: string | null,
    force = false
  ): string | null => {
    if (newExpire == null) {
      return null;
    }

    const newSchedule: AccessSchedule = { from: newExpire, to: null, accessLevel: AccessLevelType.Private };
    let replaceIndex: number | undefined;

    if (oldExpire != null) {
      const foundIndex = schedules.findIndex((x) => x.from === oldExpire);
      if (foundIndex > -1) {
        replaceIndex = foundIndex;
      } else if (!force) {
        return null;
      }
    }

    return accessUtils.validateScheduleOverlap(schedules, newSchedule, replaceIndex);
  },

  /** Validates and applies an embargo date change, returning updated schedules or an error message. */
  applyEmbargoSet: (
    accessSchedules: AccessSchedule[],
    oldEmbargo: string | null,
    newEmbargo: string | null,
    force = false
  ): { accessSchedules: AccessSchedule[]; error: string | null } => {
    const error = accessUtils.validateEmbargoSetChange(accessSchedules, oldEmbargo, newEmbargo, force);
    if (error) {
      return { accessSchedules, error };
    }

    return {
      accessSchedules: accessUtils.ensureEmbargoSet([...accessSchedules], oldEmbargo, newEmbargo, force),
      error: null,
    };
  },

  /** Validates and applies an expire date change, returning updated schedules or an error message. */
  applyExpireSet: (
    accessSchedules: AccessSchedule[],
    oldExpire: string | null,
    newExpire: string | null,
    force = false
  ): { accessSchedules: AccessSchedule[]; error: string | null } => {
    const error = accessUtils.validateExpireSetChange(accessSchedules, oldExpire, newExpire, force);
    if (error) {
      return { accessSchedules, error };
    }

    return {
      accessSchedules: accessUtils.ensureExpireSet([...accessSchedules], oldExpire, newExpire, force),
      error: null,
    };
  },

  /** Returns an error if the candidate schedule overlaps others or breaks open-ended From/To rules. */
  validateScheduleOverlap: (
    schedules: AccessSchedule[],
    candidate: AccessSchedule,
    replaceIndex?: number
  ): string | null => {
    const testSchedules =
      replaceIndex != null
        ? schedules.map((schedule, index) => (index === replaceIndex ? candidate : schedule))
        : [...schedules, candidate];

    let emptyFromCount = 0;
    let emptyToCount = 0;
    for (const schedule of testSchedules) {
      if (isOpenScheduleBound(schedule.from)) {
        emptyFromCount += 1;
      }
      if (isOpenScheduleBound(schedule.to)) {
        emptyToCount += 1;
      }
    }

    if (emptyFromCount > 1) {
      return 'Only one access schedule can have an empty From Date.';
    }
    if (emptyToCount > 1) {
      return 'Only one access schedule can have an empty To Date.';
    }

    for (let i = 0; i < testSchedules.length; i += 1) {
      for (let j = i + 1; j < testSchedules.length; j += 1) {
        if (schedulesOverlap(testSchedules[i], testSchedules[j])) {
          return 'Access schedules cannot overlap.';
        }
      }
    }

    return null;
  },

  /** Returns the embargo date from the Private schedule with an empty From date, if present. */
  resolveEmbargoDateFromSchedules: (schedules: AccessSchedule[]): string | null => {
    const schedule = schedules.find(
      (item) => isOpenScheduleBound(item.from) && !isOpenScheduleBound(item.to) && item.accessLevel === AccessLevelType.Private
    );
    return schedule?.to ?? null;
  },

  /** Returns the expire date from the Private schedule with an empty To date, if present. */
  resolveExpireDateFromSchedules: (schedules: AccessSchedule[]): string | null => {
    const schedule = schedules.find(
      (item) => !isOpenScheduleBound(item.from) && isOpenScheduleBound(item.to) && item.accessLevel === AccessLevelType.Private
    );
    return schedule?.from ?? null;
  },

  /** Writes embargo and expire form fields from the current access schedules after schedule edits. */
  syncEmbargoExpireFromSchedules: (
    schedules: AccessSchedule[],
    setValue: (name: string, value: unknown) => void,
    embargoDateField?: string,
    expireDateField?: string
  ) => {
    if (embargoDateField) {
      setValue(embargoDateField, accessUtils.resolveEmbargoDateFromSchedules(schedules));
    }
    if (expireDateField) {
      setValue(expireDateField, accessUtils.resolveExpireDateFromSchedules(schedules));
    }
  },

  getAccessLevelOptions: (ouData: OU) => {
    if (ouData?.metaData?.enablePaywall === true) {
      return AllAccessLevelOptions;
    }
    return DefaultAccessLevelOptions;
  },

  hasEditSharedAssetAccess: (permissions: Permissions | null | undefined): boolean => {
    return !!permissions?.has(Permission.EditSharedAssetAccess);
  },

  resolveContentSharingBehavior: (
    ouData: OU | null | undefined,
    contentSharingBehavior?: ContentSharingBehaviors | null
  ): ContentSharingBehaviors => {
    return contentSharingBehavior ?? ouData?.metaData?.contentSharing?.behavior ?? 'DoNotShare';
  },

  /** Resolves sharing behavior for save: per-asset override, form selection, or OU default. */
  resolveAssetContentSharingBehavior: (form: AssetForm, ouData: OU): ContentSharingBehaviors => {
    const ouDefault = ouData.metaData?.contentSharing?.behavior ?? 'DoNotShare';
    if (form.originalAssetData?.data?.contentSharing != null) {
      return (form.contentSharingBehavior ??
        form.originalAssetData.data.contentSharing.behavior ??
        ouDefault) as ContentSharingBehaviors;
    }
    return (form.contentSharingBehavior ?? ouDefault) as ContentSharingBehaviors;
  },

  isContentSharingVisible: (
    ouData: OU | null | undefined,
    schemaType: SchemaType | string | null | undefined,
    source: string | null | undefined
  ): boolean => {
    return (
      ouData?.metaData?.enableCrossOuSharing === true &&
      schemaType === SchemaType.Video &&
      source !== AssetSource.NestedChannel
    );
  },

  canSaveContentSharing: (
    permissions: Permissions | null | undefined,
    ouData: OU | null | undefined,
    schemaType: SchemaType | string | null | undefined,
    source: string | null | undefined
  ): boolean => {
    return accessUtils.isContentSharingVisible(ouData, schemaType, source);
  },

  getAccessScheduleCount: (accessSettings?: AccessSettings | null): number =>
    accessSettings?.temporal?.accessSchedules?.length ?? 0,

  /** True when a user without EditSharedAssetAccess moves from Do Not Share to Share With Parent Partner and schedules exist. */
  shouldConfirmShareWithParentPortalScheduleClear: (
    permissions: Permissions | null | undefined,
    oldBehavior: ContentSharingBehaviors | string | null | undefined,
    newBehavior: ContentSharingBehaviors | string | null | undefined,
    accessSettings?: AccessSettings | null
  ): boolean => {
    return (
      !accessUtils.hasEditSharedAssetAccess(permissions) &&
      (oldBehavior ?? 'DoNotShare') === 'DoNotShare' &&
      newBehavior === 'ShareWithParentPortal' &&
      accessUtils.getAccessScheduleCount(accessSettings) > 0
    );
  },

  SHARE_WITH_PARENT_PORTAL_SCHEDULE_CLEAR_CONFIRM:
    'Changing sharing to Share With Parent Partner will completely clear all access schedules. Do you want to continue?',

  /** Clears access schedules and synced embargo/expire fields after sharing moves to Share With Parent Partner. */
  clearAccessSchedulesInForm: (
    setValue: (name: string, value: unknown) => void,
    accessSettingsPath = 'accessSettings'
  ) => {
    setValue(`${accessSettingsPath}.temporal.accessSchedules`, []);
    accessUtils.syncEmbargoExpireFromSchedules([], setValue, 'embargoDate', 'expireDate');
  },

  /** Locks the Access tab and Details embargo/expire for users without EditSharedAssetAccess when sharing with parent partner. */
  lockAccessControls: (
    permissions: Permissions | null | undefined,
    ouData: OU | null | undefined,
    contentSharingBehavior?: ContentSharingBehaviors | null,
    schemaType?: string | null,
    source?: string | null
  ): boolean => {
    if (accessUtils.hasEditSharedAssetAccess(permissions)) {
      return false;
    }
    if (!accessUtils.isContentSharingVisible(ouData, schemaType, source)) {
      return false;
    }
    const effectiveBehavior = accessUtils.resolveContentSharingBehavior(ouData, contentSharingBehavior);
    return effectiveBehavior === 'ShareWithParentPortal';
  },
};

export { accessUtils };
